import { Injectable } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import * as PaymentSelectors from '@uzurv/rideWss/app/store/payment/payment.selectors';
import * as RiderAuthenticationActions from '@uzurv/rideWss/app/store/rider-authentication/rider-authentication.action';
import * as RiderAuthenticationSelectors from '@uzurv/rideWss/app/store/rider-authentication/rider-authentication.selector';
import * as RidesActions from '@uzurv/rideWss/app/store/rides/rides.actions';
import * as RidesSelectors from '@uzurv/rideWss/app/store/rides/rides.selectors';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class RidesFacade {
  // RiderAuthentication
  readonly authenticationComplete$ = this.actions$.pipe(ofType(RiderAuthenticationActions.riderAuthenticationComplete));
  readonly authenticationStatus$ = this.store$.select(RiderAuthenticationSelectors.getAuthenticationStatus);
  readonly isRefreshingAuthenticatedRider$ = this.store$.select(RiderAuthenticationSelectors.getIsRefreshingAuthenticatedRider);
  readonly isRiderSuspended$ = this.store$.select(RiderAuthenticationSelectors.getIsRiderSuspended);
  readonly refreshAuthenticatedRiderSuccess$ = this.actions$.pipe(ofType(RiderAuthenticationActions.refreshAuthenticatedRiderSuccess));

  // Payment
  readonly outstandingCharges$ = this.store$.select(PaymentSelectors.getOutstandingCharges);

  // Rider
  readonly isRiderEligible$ = this.store$.select(RidesSelectors.isRiderEligible);
  readonly selectedOrganizationRiderListItem$ = this.store$.select(RidesSelectors.getSelectedOrganizationRiderListItem);

  // Rides
  readonly initialUpcomingRidesLoading$ = this.store$.select(RidesSelectors.getInitialUpcomingRidesLoading);
  readonly isCreateRideEnabled$ = this.store$.select(RidesSelectors.getIsCreateRideEnabled);
  readonly isFetchingUpcomingRideListItems$ = this.store$.select(RidesSelectors.isFetchingUpcomingRideListItems);
  readonly isInitialOrganizationRiderListItemsLoaded$ = this.store$.select(RidesSelectors.getIsInitialOrganizationRiderListItemsLoaded);
  readonly upcomingRideGroups$ = this.store$.select(RidesSelectors.getUpcomingRideGroups);

  readonly fetchUpcomingRideListItemsSuccessFuture$ = this.actions$.pipe(ofType(RidesActions.fetchUpcomingRideListItemsSuccessFuture));
  readonly fetchUpcomingRideListItemsFail$ = this.actions$.pipe(ofType(RidesActions.fetchUpcomingRideListItemsFail));

  // Facade Selectors
  readonly isRiderIneligible$: Observable<boolean> = combineLatest([
    this.isRiderSuspended$,
    this.outstandingCharges$,
    this.isRiderEligible$,
    this.isCreateRideEnabled$
  ]).pipe(
    map(
      ([isRiderSuspended, outstandingCharges, isRiderEligible, isCreateRideEnabled]): boolean =>
        isRiderSuspended || outstandingCharges?.length > 0 || !isRiderEligible || !isCreateRideEnabled
    )
  );

  // Facade Actions

  /**
   * Rehydrate the rider authentication state.
   */
  rehydrateState(): void {
    this.store$.dispatch(RiderAuthenticationActions.rehydrateState());
  }

  /**
   * Dispatches action to logout current user.
   */
  logout(): void {
    this.store$.dispatch(RiderAuthenticationActions.logout());
  }

  /**
   * Refreshes authenticatedRider for updated values for things like isRiderIneligible.
   *
   * @param isFromAuthentication
   */
  refreshAuthenticatedRider(isFromAuthentication: boolean): void {
    this.store$.dispatch(RiderAuthenticationActions.refreshAuthenticatedRider({ isFromAuthentication: isFromAuthentication }));
  }

  /**
   * Fetches rider's organization rider list items.
   */
  fetchOrganizationRiderListItems(): void {
    this.store$.dispatch(RidesActions.fetchOrganizationRiderListItems());
  }

  /**
   * Fetches upcoming ride list items based on pageNumber & pageSize.
   *
   * @param pageNumber
   * @param pageSize
   */
  fetchUpcomingRideListItems(pageNumber: number, pageSize: number): void {
    this.store$.dispatch(RidesActions.fetchUpcomingRideListItems({ pageNumber: pageNumber, pageSize: pageSize }));
  }

  constructor(private actions$: Actions, private store$: Store) {}
}
